import AdminAreaProtectedRoute from "./components/route/AdminAreaProtectedRoute";
import AppRoute from "./components/route/AppRoute";
import {
  nonProtectedAdminRoutes,
  protectedRoutes,
} from "@/configs/routes.config";
import appConfig from "@/configs/app.config";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
} from "react-router-dom";
import RouteErrorBoundary from "@/custolink-components/errors/RouteErrorBoundary";
import ErrorBoundary from "@/custolink-components/errors/ErrorBoundary";
import RouteNotFound from "@/custolink-components/errors/RouteNotFound";
import AdminAreaAuthorityGuard from "@/components/route/AuthorityGuard";
import AssetFrontendHomeRoute from "@/custolink-views/asset_frontend/redesigns/AssetFrontendHomeRoute";
import AdminAreaNonProtectedPageContainer from "@/custolink-components/layout/AdminAreaNonProtectedPageContainer";
import Simple from "@/custolink-views/asset_frontend/SimpleLayout";
import AdminAreaProtectedPageContainer from "./custolink-components/layout/AdminAreaProtectedPageContainer";
import VideoByLink from "@/custolink-views/asset_frontend/video_by_link/VideoByLink";
import VerifyEmailForEndUser from "@/custolink-views/end_user/VerifyEmailForEndUser";
import { Context, contextAndAuthLoader } from "@/ContextAndAuthLoader";
import AdminAreaWrapper from "@/custolink-views/admin/AdminArea";
import { uiPaths } from "@/custolink-transpiled_by_server/ui_paths";
import AssetFrontendArea from "./custolink-views/asset_frontend/AssetFrontendArea";
import ResetPasswordForEndUser from "@/custolink-views/end_user/ResetPasswordForEndUser";
import AssetFilesDownloadLink from "@/custolink-views/etc/AssetFilesDownloadLink";
import AssetCustomView from "@/custolink-views/asset_frontend/asset_view/AssetCustomView";
import AssetDefaultView from "@/custolink-views/asset_frontend/asset_view/AssetDefaultView";
import useUiContext from "@/custolink-hooks/useUiContext";
import VideoRoomPage from "@/custolink-views/shared/VideoRoomPage";
import RemoteSupportRoomPage from "@/custolink-newdesign/pages/support/room/RemoteSupportRoomPage";
import { isNewDesign } from "@/custolink-newdesign/flag";
import { isAssetFrontendNewDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";

export const OrganisationAllRoutes = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        path={"/cstln/cp/protected"}
        loader={() => contextAndAuthLoader(true)}
        element={<Context />}
      >

      </Route>
      <Route
        path={"/admin"}
        loader={() => contextAndAuthLoader(true)}
        element={<Context />}
        errorElement={<RouteErrorBoundary />}
      >
        <Route
          path={""}
          element={
            <AdminAreaWrapper>
              <Outlet />
            </AdminAreaWrapper>
          }
        >
          <Route
            path=""
            element={<AdminAreaProtectedRoute />}
            errorElement={<RouteErrorBoundary />}
          >
            <Route
              path=""
              element={<NavigateToCorrectHome />}
            />
            {protectedRoutes.map((route, index) => {
              // The new design picks the redesigned page where one exists. The
              // new pages carry their own header through PageShell /
              // SettingsShell, so AdminAreaProtectedPageContainer - which draws
              // the old header and gutters from route.meta - is skipped for
              // them. Routes with no componentNewDesign are untouched.
              const useNew = isNewDesign() && route.componentNewDesign != null;
              const component = useNew
                ? route.componentNewDesign!
                : route.component;

              const page = (
                // Mounts inside AdminAreaWrapper -> AppShell's content column
                // (new design) whether or not this route itself converted -
                // inline tells ErrorBoundary's new-design fallback to fill
                // that column rather than claim a fresh viewport.
                <ErrorBoundary inline>
                  <AppRoute
                    routeKey={route.key}
                    component={component}
                    {...route.meta}
                  />
                </ErrorBoundary>
              );

              return (
                <Route
                  key={route.key + index}
                  path={"/admin" + route.path}
                  element={
                    <AdminAreaAuthorityGuard authority={route.authority}>
                      {useNew ? (
                        page
                      ) : (
                        <AdminAreaProtectedPageContainer {...route.meta}>
                          {page}
                        </AdminAreaProtectedPageContainer>
                      )}
                    </AdminAreaAuthorityGuard>
                  }
                />
              );
            })}
            <Route path="*" element={<Navigate replace to="/" />} />
          </Route>
        </Route>
        <Route path="/admin">
          {nonProtectedAdminRoutes.map((route) => {
            // The redesigned auth pages are full-bleed split covers
            // (flow AuthShell.tsx), so they replace
            // AdminAreaNonProtectedPageContainer rather than render inside it.
            const useNew = isNewDesign() && route.componentNewDesign != null;
            const page = (
              <ErrorBoundary>
                <AppRoute
                  routeKey={route.key}
                  component={useNew ? route.componentNewDesign! : route.component}
                  {...route.meta}
                />
              </ErrorBoundary>
            );

            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  useNew ? (
                    page
                  ) : (
                    <AdminAreaNonProtectedPageContainer>
                      {page}
                    </AdminAreaNonProtectedPageContainer>
                  )
                }
              />
            );
          })}
        </Route>
        <Route element={<AdminAreaProtectedRoute />}>
          {/* The worker's own popup, opened by reserveWorkerVideoRoom - logged-in, admin-area
              surface, so this follows the admin switch, not the asset-frontend one. */}
          <Route
            path="/admin/video-room"
            element={isNewDesign() ? <RemoteSupportRoomPage /> : <VideoRoomPage />}
          />
        </Route>
      </Route>

      <Route
        path={"/"}
        loader={() => contextAndAuthLoader(false)}
        element={<Context />}
        errorElement={<RouteErrorBoundary />}
      >
        <Route
          path="/"
          element={
            <Simple>
              <Outlet />
            </Simple>
          }
          errorElement={<RouteErrorBoundary />}
        >
          <Route path="" element={<AssetFrontendArea />}>
            <Route
              path=""
              element={<AssetFrontendHomeRoute />}
              errorElement={<RouteErrorBoundary />}
            />
            {
              //<Route
              //path="/camera"
              //element={<Scan />}
              //errorElement={<RouteErrorBoundary />}
              ///>
            }
            <Route
              path="/asset/:serialNumber"
              element={<AssetDefaultView />}
              errorElement={<RouteErrorBoundary />}
            />
            <Route
              path="/view/:viewUiSlug/:viewValue"
              element={<AssetCustomView />}
              errorElement={<RouteErrorBoundary />}
            />
            <Route
              path="/video_by_link/:roomId"
              element={<VideoByLink />}
              errorElement={<RouteErrorBoundary />}
            />
            {/* Public, reached without logging in - predates VideoByLink rendering the room
                inline (see its header comment); reserveWorkerVideoRoom only ever opens
                /admin/video-room now, so nothing currently links here, but it still sits in
                the public asset-frontend tree, not the admin one. Follows the asset-frontend
                switch, not the admin switch, for the same reason as VideoByLink and
                VideoCallAppPageComponent. */}
            <Route
              path="/video-room"
              element={isAssetFrontendNewDesign() ? <RemoteSupportRoomPage /> : <VideoRoomPage />}
              errorElement={<RouteErrorBoundary />}
            />
            <Route
              path={uiPaths.verifyEmailEndUserPathPrefix + "/:token"}
              element={<VerifyEmailForEndUser />}
            />
            <Route
              path={uiPaths.resetPasswordEndUserPathPrefix + "/:token"}
              element={<ResetPasswordForEndUser />}
            />
            <Route
              path={uiPaths.assetFilesTempDownloadLinkPathPrefix + "/:token"}
              element={<AssetFilesDownloadLink />}
            />
            {/* Unknown path under "/" (e.g. a typo'd link, or AuthorityGuard's
                /access-denied, which does not resolve to a route of its own -
                see AuthorityGuard.tsx). Splat routes rank lowest regardless of
                declaration order, so this cannot shadow any route above it. */}
            <Route path="*" element={<RouteNotFound />} />
          </Route>
        </Route>
      </Route>
    </>,
  ),
);

function NavigateToCorrectHome() {
  const { controlpanelContext } = useUiContext();

  if (controlpanelContext) {
    return <Navigate replace to={appConfig.authenticatedControlpanelEntryPath} />;
  }

  return <Navigate replace to={appConfig.authenticatedAdminEntryPath} />;
}

export default OrganisationAllRoutes;
